<template>
  <div>
    <FocusIndicator
      card-selector=".gid-table-infinite tbody tr"
      :name-extractor="extractJobName"
    />

    <!-- Job console tabs: List is the permanent first tab; opening a job's
         Details adds a tab (up to 5, least-recently-viewed is evicted).
         A deep link (/jobs/<job-name>) opens that job as an active tab on top
         of the normal list — it is a shortcut into the console, not a view of
         its own. -->
    <div class="gid-job-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="gid-job-tab gid-job-tab--list"
        :class="{ 'gid-job-tab--active': activeJobId === null }"
        :aria-selected="activeJobId === null"
        @click="switchTab('list')"
      >
        <span class="material-icons gid-job-tab__icon">list</span>
        <span>{{ $t('job.console.list') }}</span>
      </button>
      <button
        v-for="job in openJobs"
        :key="job.api_id"
        type="button"
        role="tab"
        class="gid-job-tab"
        :class="{ 'gid-job-tab--active': activeJobId === job.api_id }"
        :aria-selected="activeJobId === job.api_id"
        :title="job.jobNo + (job.customer ? ' · ' + job.customer : '')"
        @click="activateJob(job.api_id)"
      >
        <span
          v-if="job.touched"
          class="gid-job-tab__dirty"
          :title="$t('job.console.unsaved')"
        >
          ●
        </span>
        <span class="gid-job-tab__no">{{ job.jobNo }}</span>
        <span v-if="job.customer" class="gid-job-tab__cust">
          · {{ job.customer }}
        </span>
        <span
          class="gid-job-tab__close"
          role="button"
          :aria-label="$t('job.console.close')"
          @click.stop="closeJob(job.api_id)"
        >
          ×
        </span>
      </button>
    </div>

    <!-- List pane (hidden, not unmounted, when a job tab is active) -->
    <div v-show="activeJobId === null">
      <!-- Predefined filter sets. One is active at a time, chosen from a grouped
           dropdown. Driven by the shared registry; grows automatically. -->
      <div class="gid-job-presets">
        <span class="gid-job-presets__label">
          {{ $t('job-presets.view') }}:
        </span>
        <b-dropdown
          variant="light"
          size="sm"
          class="gid-job-presets__dd"
          menu-class="gid-job-presets__menu"
          boundary="viewport"
        >
          <template #button-content>
            <span class="gid-preset-item__label">{{ activePresetLabel }}</span>
            <span
              v-if="activeView && presetRevenue(activeView)"
              class="gid-preset-item__revenue ml-2"
              :title="presetRevenueTitle(activeView)"
            >
              {{ presetRevenue(activeView) }}
            </span>
            <b-badge
              v-if="activeView && presetCounts[activeView] != null"
              pill
              class="gid-preset-item__count ml-2"
            >
              {{ presetCounts[activeView] }}
            </b-badge>
          </template>
          <b-dropdown-item-button
            :active="!activeView"
            @click="selectPreset(null)"
          >
            {{ $t('job-presets.all') }}
          </b-dropdown-item-button>
          <template v-for="grp in presetGroups">
            <b-dropdown-header :key="`h-${grp.group}`">
              {{ $t(`job-presets.groups.${grp.group}`) }}
            </b-dropdown-header>
            <b-dropdown-item-button
              v-for="preset in grp.presets"
              :key="preset.id"
              :active="activeView === preset.id"
              @click="selectPreset(preset.id)"
            >
              <span class="gid-preset-item">
                <span class="gid-preset-item__label">
                  {{ $t(`job-presets.titles.${preset.id}`) }}
                </span>
                <span
                  v-if="presetRevenue(preset.id)"
                  class="gid-preset-item__revenue"
                  :title="presetRevenueTitle(preset.id)"
                >
                  {{ presetRevenue(preset.id) }}
                </span>
                <b-badge
                  v-if="presetCounts[preset.id] != null"
                  pill
                  class="gid-preset-item__count"
                >
                  {{ presetCounts[preset.id] }}
                </b-badge>
              </span>
            </b-dropdown-item-button>
          </template>
        </b-dropdown>
        <!-- Reference guide: what every job status and substatus means.
             The document itself is a static, self-contained page in public/;
             the modal just frames it so admins can keep it open while working. -->
        <button
          type="button"
          class="gid-status-guide__btn"
          :title="$t('job-status-guide.tooltip')"
          :aria-label="$t('job-status-guide.tooltip')"
          @click="openStatusGuide"
        >
          <span class="material-icons">help_outline</span>
        </button>
      </div>
      <b-card
        bg-variant="light"
        ref="stickyFilters"
        class="my-3"
        :style="stickyStyles"
      >
        <b-row>
          <b-col cols="12" lg="1">
            <b-form-group>
              <template #label>
                <span style="color: transparent">.</span>
              </template>
              <b-button
                block
                class="mr-md-3"
                variant="primary"
                to="/jobs/create"
              >
                New
              </b-button>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" lg="2">
            <b-form-group :label="`${$t('filter')}:`" class="mb-md-0">
              <b-input-group>
                <b-form-input
                  v-model="filterSearch"
                  debounce="400"
                  :placeholder="$t('type-to-search')"
                />
                <b-input-group-append v-if="filterSearch">
                  <b-btn @click="filterSearch = ''">&times;</b-btn>
                </b-input-group-append>
              </b-input-group>
              <b-button
                size="sm"
                variant="light"
                class="mt-2"
                @click="loadData()"
              >
                <span
                  class="material-icons"
                  style="vertical-align: middle; line-height: 1"
                >
                  refresh
                </span>
              </b-button>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" lg="3">
            <b-form-group class="mb-md-0">
              <template #label>
                {{ $t('status') }}
                <b-badge
                  pill
                  variant="primary"
                  style="cursor: pointer"
                  @click="selectActiveStatuses"
                >
                  ✓
                </b-badge>
                <b-badge
                  pill
                  variant="secondary ml-1"
                  style="cursor: pointer"
                  @click="filterStatus = []"
                >
                  𐄂
                </b-badge>
                :
              </template>
              <multiselect
                v-model="filterStatus"
                :options="statuses"
                :multiple="true"
                track-by="value"
                label="text"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" lg="2">
            <b-form-group :label="`${$t('owner')}:`" class="mb-md-0">
              <b-form-select v-model="filterOwner" :options="owners">
                <template slot="first">
                  <option :value="null">-- {{ $t('all') }} --</option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" lg="2"></b-col>
          <b-col cols="12" md="6" lg="2">
            <b-form-checkbox v-model="filterTest">
              {{ $t('include-test-jobs') }}
            </b-form-checkbox>
            <b-form-checkbox
              class="pt-1"
              v-e2e:filterUseAdvancedSearch
              v-model="filterUseAdvancedSearch"
            >
              {{ $t('filters.advanced.toggle') }}
            </b-form-checkbox>
            <b-form-checkbox
              class="pt-1"
              v-e2e:filterUseQuickSearch
              v-model="filterUseQuickSearch"
            >
              {{ $t('filters.quick.toggle') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <advanced-search
          v-if="filterUseAdvancedSearch"
          v-model="filterAdvancedSearch"
          :filterOptions="advancedFilterOptions"
        />
        <div v-if="filterUseQuickSearch">
          <quick-search
            class="overflow-auto text-nowrap"
            v-model="filterQuickSearch"
            logic="and"
            :filter-combinations="filterCombinations"
          />
        </div>
      </b-card>
      <b-table
        :show-empty="!loading"
        striped
        stacked="md"
        class="gid-table-infinite"
        ref="wrapper"
        :items="items"
        :fields="tableFields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDir"
        no-local-sorting
      >
        <template #empty>
          <p class="text-muted text-center m-3">
            {{ $t('list-no-results-filter-hint') }}
          </p>
        </template>
        <template #cell(partner.name)="{ item }">
          <template v-if="item.job.partner">
            <p
              role="button"
              class="clickable-text"
              @click="handlePartnerClick(item)"
            >
              {{ item.partner.name }}
            </p>
            <b-badge v-if="item.partner.is_partner_paused" variant="warning">
              {{ $t('partner-pause-generic.paused') }}
            </b-badge>
            <p class="my-2" v-if="item.partner.name">
              <b-badge
                :variant="
                  partnerExperienceLevel(item.partner.completed_jobs).variant
                "
              >
                {{ partnerExperienceLevel(item.partner.completed_jobs).text }}
              </b-badge>
            </p>
            <call-contact
              :job-api-id="item.job.api_id"
              :contact-list="jobsContacts[item.api_id]"
              :role-filter="partnerRole"
              @call-initiated="showCommentModal"
            />
          </template>
          <b-link v-else @click.prevent="$refs.partnerView.viewPartners(item)">
            {{ item.num_partners_offer_is_visible_to }}
            ({{ item.num_partners_potential }})
          </b-link>
        </template>
        <template #cell(job.name)="{ item }">
          <a
            :label="item.job.name"
            :href="`../jobs/${item.job.name}`"
            class="text-nowrap"
            :target="items.length > 1 ? '_blank' : '_self'"
          >
            {{ item.job.name }}
          </a>
          <b-icon-clipboard
            class="icon-grad"
            font-scale="0.8"
            role="button"
            @click="copyToClipboard(item.job.name, true)"
          />
          <small
            v-if="item.job.eid"
            class="text-muted d-block text-break"
            role="button"
            @click="copyToClipboard(item.job.eid, false)"
          >
            EID: {{ item.job.eid }}
          </small>
          <div
            v-if="item.job.invoicing_eid"
            role="button"
            @click="copyToClipboard(item.job.invoicing_eid, false)"
          >
            <small class="text-muted">
              Inv. EID: {{ item.job.invoicing_eid }}
            </small>
          </div>
          <div
            v-if="item.job.partner_eid"
            role="button"
            @click="copyToClipboard(item.job.partner_eid, false)"
          >
            <small class="text-muted">
              Partner EID: {{ item.job.partner_eid }}
            </small>
          </div>
          <div
            v-if="item.job.s4c_order_id"
            role="button"
            :title="$t('job.s4c-open-hint')"
            @click="onS4cClick(item)"
            @dblclick="onS4cDblClick(item)"
          >
            <small class="text-muted">S4C: {{ item.job.s4c_order_id }}</small>
          </div>
          <div v-if="item.is_invoicing_scheduled">
            <b-badge variant="info">
              {{ isJobPartiallyInvoiced(item) }}
            </b-badge>
          </div>
          <small
            v-if="item.job.affiliate_id"
            class="text-muted d-block text-break"
          >
            AID: {{ item.job.affiliate_id }}
          </small>
          <div v-if="item.productOrder">
            <b-badge :variant="item.productOrder.variant">
              {{ $t(`job.product-order-status.${item.productOrder.status}`) }}
            </b-badge>
            <small v-if="item.productOrder.shipTo.length > 0">
              Ship to:
              <b-badge
                v-for="shipTarget in item.productOrder.shipTo"
                :key="shipTarget"
              >
                {{ shipTarget }}
              </b-badge>
            </small>
            <div
              class="d-flex flex-column font-small"
              v-if="item.productOrder.trackingLink.length > 0"
            >
              <span
                v-for="(trackingLink, index) in item.productOrder.trackingLink"
                :key="trackingLink"
              >
                <b-icon-box class="mr-2" />
                <a :href="trackingLink" target="_blank">
                  {{ $t(`job.tracking-link`) }} {{ index ? `(${index})` : '' }}
                </a>
              </span>
            </div>
          </div>
          <div v-if="item.product_serial_number">
            <span>
              <b-icon-upc-scan class="mr-2" />
              <span>{{ item.product_serial_number }}</span>
            </span>
          </div>
          <div v-if="item.job.protocol_submitted">
            <b-badge variant="success">
              {{ $t('job.protocol-submitted') }}
            </b-badge>
          </div>
          <small v-if="Object.keys(jobRatings).length">
            <RatingWidget context="job" v-bind="jobRatings[item.job.api_id]" />
          </small>
          <div>
            <a href="#" @click.prevent="getQuotePath(item.sfid)">
              {{ $t('view-quote') }}
            </a>
          </div>
          <div v-if="item.files.customer_quote">
            <a
              :href="`/api/media/file/${item.files.customer_quote.paths[0]}?jwt=${access_token}`"
              target="_blank"
            >
              {{ $t('accepted-quote') }}
            </a>
          </div>
          <div v-if="showBrandQuoteLinks(item)">
            <a href="#" @click.prevent="getBrandQuotePath(item.sfid)">
              {{ $t('view-brand-quote') }}
            </a>
          </div>
          <div v-if="item.files.brand_quote">
            <a
              :href="`/api/media/file/${item.files.brand_quote.paths[0]}?jwt=${access_token}`"
              target="_blank"
            >
              {{ $t('accepted-brand-quote') }}
            </a>
          </div>
          <b-badge v-if="hasOwnInstaller(item)" variant="dark">
            {{ $t(`job.has-own-installer`) }}
          </b-badge>
          <div v-if="isAlternative(item)">
            <b-badge
              :style="{ background: '#2c757c', color: 'white' }"
              :id="`alternate_quote-${item.id}`"
            >
              {{ $t(`job.is-alternative`) }}
            </b-badge>
            <b-popover
              triggers="click blur"
              :target="`alternate_quote-${item.id}`"
              title="Alternative Jobs"
            >
              <div
                v-for="(alternate, index) in getAlternativeLinks(item)"
                :key="alternate"
              >
                <a target="_blank" :href="`/jobs/${alternate}`">
                  Alternative ({{ index }})
                </a>
              </div>
            </b-popover>
          </div>
          <b-badge
            :style="isReclamation(item).styles"
            v-if="isReclamation(item)"
          >
            {{ isReclamation(item).text }}
          </b-badge>
          <b-badge
            :style="hasAutoCreatedJob(item).styles"
            v-if="hasAutoCreatedJob(item)"
          >
            {{ hasAutoCreatedJob(item).text }}
          </b-badge>
          <b-badge
            :style="hasNextStepJob(item).styles"
            v-if="hasNextStepJob(item)"
          >
            {{ hasNextStepJob(item).text }}
          </b-badge>
          <JobTypes :job="item" />
        </template>
        <template #cell(opportunity)="{ item }">
          <span :id="item.sfid">
            <span
              role="button"
              class="clickable-text"
              @click="handleOpportunityClick(item)"
            >
              {{ item.opportunity.name }}
            </span>
            <b-tooltip
              v-if="opportunityHint(item)"
              :target="item.sfid"
              triggers="hover"
            >
              {{ opportunityHint(item) }}
            </b-tooltip>
          </span>
          <div>
            <b-badge v-if="isPrepayment(item)">
              {{ $t('prepayment') }}: {{ item.opportunity.prepayment }}
            </b-badge>
          </div>
          <span
            v-if="item.project"
            class="clickable-text mt-1 d-inline-block"
            style="font-size: 0.85rem; word-break: break-all"
            role="button"
            @click="handleProjectClick(item)"
          >
            {{ item.project.name }}
          </span>
          <call-contact
            :job-api-id="item.job.api_id"
            :contact-list="jobsContacts[item.api_id]"
            :role-filter="brandRole"
            @call-initiated="showCommentModal"
          />
        </template>
        <template #cell(job.appointment_start_timestamp)="{ item }">
          <div role="button">
            <span
              @click="
                copyToClipboard(
                  $moment(item.job.appointment_start_timestamp).format('L'),
                )
              "
            >
              {{
                item.job.appointment_start_timestamp
                  ? $moment(item.job.appointment_start_timestamp).format('L')
                  : '-'
              }}
            </span>
            <span
              @click="
                copyToClipboard(
                  $moment(item.job.appointment_end_timestamp).format('L'),
                )
              "
              v-if="
                $moment(item.job.appointment_end_timestamp).format('L') !==
                $moment(item.job.appointment_start_timestamp).format('L')
              "
            >
              {{
                item.job.appointment_end_timestamp
                  ? '- ' +
                    $moment(item.job.appointment_end_timestamp).format('L')
                  : ''
              }}
            </span>
          </div>
          <br />
          {{
            greaterDateFormatted(
              item.job.readiness_date,
              item.job.date_expected,
            )
          }}
          <br />
          <strong>
            {{ latestStatusChangeFormatted(item) }}
            {{
              item.status_description?.gid_name?.[locale] ||
              item.status_description?.gid_name?.['en'] ||
              item.status
            }}
          </strong>
          <div v-for="(status, idx) in compoundStateItems(item)" :key="idx">
            <b-badge variant="warning" class="text-wrap">
              {{ status.name }}
            </b-badge>
            <div v-if="status.details" class="small text-muted mt-1">
              {{ status.details }}
            </div>
          </div>
          <!-- GID-2563: Fahrzeug Lieferdatum — moved here from its own column -->
          <div
            v-if="item.properties && item.properties.car_delivery"
            class="small text-muted mt-1"
          >
            {{ $t('job.table.heading.car_delivery') }}:
            {{ $moment(item.properties.car_delivery).format('L') }}
          </div>
          <small
            v-if="getCarTypeExist(item)"
            class="text-muted d-block text-break"
          >
            {{ $t('type') }}: {{ getCarType(item.properties.car_type) }}
          </small>
          <small
            v-if="getCarChargingTypeExist(item)"
            class="text-muted d-block text-break"
          >
            {{ $t('charging-opportunity') }}:
            {{ getChargingType(item.properties.charging_opportunity) }}
          </small>
          <div class="d-flex" v-if="shouldDisplayWallboxScore(item)">
            <DaysSinceLastChange
              v-if="item.dkv_wallbox_priority_score"
              class="ml-3"
              :days="item.dkv_wallbox_priority_score"
            />
            <span
              class="mx-2"
              v-if="getUrgentReason(item)"
              v-b-tooltip.hover
              :title="getUrgentReason(item)"
            >
              ⚠️
            </span>
          </div>
        </template>
        <template #cell(days_since_last_status_change)="{ item }">
          <div class="d-flex">
            <DaysSinceLastChange
              v-if="showDaysSinceLastStatusChange(item)"
              :days="item.days_since_last_status_change"
            />
            <DaysSinceLastChange
              v-if="item.actions_taken"
              class="ml-3"
              context="action"
              :days="item.actions_taken"
              :action_date="item.latest_action"
            />
          </div>
        </template>
        <template #cell(job._price)="{ item }">
          <job-price :job_view="item" />
        </template>
        <template #cell(job.created_date)="{ item }">
          {{
            item.job.created_date
              ? $moment(item.job.created_date).format('L')
              : '-'
          }}
          <b-badge v-if="isDKVMissingData(item)" variant="danger">
            Missing data
          </b-badge>
          <b-badge
            v-if="item.installation && item.installation.incident"
            variant="danger"
          >
            Fehlermeldung
          </b-badge>
        </template>
        <template #cell(customer._fullname)="{ item }">
          <div
            role="button"
            class="clickable-text"
            @click="handleCustomerClick(item)"
          >
            {{
              formatCustomerTitle(item.customer.title, item.customer.salutation)
            }}
            {{ item.customer.first_name }} {{ item.customer.last_name }}
          </div>
          <div
            v-if="
              item.job_contact_person.first_name &&
              item.job_contact_person.last_name
            "
            class="mb-0"
          >
            <p class="mb-0">
              <strong>{{ $t('inputs.contact_person.title') }}:</strong>
            </p>
            <p class="my-0">
              {{ item.job_contact_person.first_name }}
              {{ item.job_contact_person.last_name }}
            </p>
            <call-contact
              :job-api-id="item.job.api_id"
              :contact-list="[
                {
                  phone: item.job_contact_person.phone,
                  jobRole: customerRole[0],
                  firstName: item.job_contact_person.first_name,
                  lastName: item.job_contact_person.last_name,
                },
              ]"
              :role-filter="customerRole"
              @call-initiated="showCommentModal"
            />
          </div>
          <call-contact
            :job-api-id="item.job.api_id"
            v-else
            :contact-list="jobsContacts[item.api_id]"
            :role-filter="customerRole"
            @call-initiated="showCommentModal"
          />
        </template>
        <template #cell(actions)="row">
          <div class="text-nowrap">
            <job-docs-dropdown
              class="mr-1"
              :files="row.item.files"
              :job-name="row.item.job.name"
              :job-sfid="row.item.sfid"
              :jmf-picklist="jmfPicklist"
            />
            <b-button
              v-e2e:jobDetails
              size="sm"
              class="mr-1"
              variant="primary"
              @click.stop="openJobTab(row.item)"
            >
              {{ $t('job.console.details') }}
            </b-button>
            <b-button
              variant="light"
              size="sm"
              @click.stop="$refs.logView.showLog(row.item.job.sfid)"
            >
              Log
            </b-button>
          </div>
          <div class="mt-3">
            <b-button
              v-if="shouldDisplayInternalProtocol(row.item.job)"
              variant="primary"
              size="sm"
              @click.prevent="getInternalProtocolPath(row.item.job.sfid)"
            >
              {{ $t('job.internal-protocol') }}
            </b-button>
          </div>
        </template>
        <template #custom-foot="{ columns }" v-if="loading">
          <b-tr v-for="row in perPage" :key="row">
            <b-td v-for="col in columns" :key="col">
              <b-skeleton :width="`${Math.round(Math.random() * 50) + 50}%`" />
            </b-td>
          </b-tr>
        </template>
      </b-table>
      <div class="gid-results-count__holder d-flex">
        <b-button
          pill
          v-if="stickyFiltersButton"
          @click="toggleSitckyFilters"
          :variant="stickyFiltersVisible ? 'primary' : 'secondary'"
          class="mr-2"
        >
          <BIconSliders />
        </b-button>
        <div class="rounded-pill gid-results-count">
          <span :class="{ 'gid-results-count--loading': loading }">
            <template v-if="totalRows !== null">
              {{ $t('results') }}: {{ items.length }} / {{ totalRows || 0 }}
            </template>
            <BIconThreeDots v-else />
          </span>
          <!-- Revenue / cost over the whole filtered set (not just the loaded
               page). Arrives separately from the rows, so it appears once the
               aggregate lands and stays absent if it failed. -->
          <template v-if="totals">
            <span
              class="gid-results-count__money"
              :title="
                $t('job.totals.revenue-exact', {
                  count: totals.count,
                  amount: formatEur(totals.revenue),
                })
              "
            >
              {{ $t('job.totals.revenue') }}: {{ formatKEur(totals.revenue) }}
            </span>
            <span
              class="gid-results-count__money"
              :title="
                $t('job.totals.cost-exact', {
                  count: totals.count,
                  amount: formatEur(totals.costs),
                })
              "
            >
              {{ $t('job.totals.cost') }}: {{ formatKEur(totals.costs) }}
            </span>
          </template>
        </div>
      </div>
      <b-alert v-if="error !== null" variant="danger" class="text-left" show>
        <h4 class="alert-heading">{{ $t('_errors.network.title') }}</h4>
        <p>{{ error.message }}</p>
        <b-collapse id="error-details" class="my-2">
          <b-card>
            <pre>{{ error.details }}</pre>
          </b-card>
        </b-collapse>
        <b-btn v-b-toggle.error-details>
          {{ $t('login.buttons.error-details') }}
        </b-btn>
      </b-alert>
    </div>

    <!-- Open job panes: each open job stays mounted (state preserved) and is
         shown only when its tab is active. Native input/change bubbling flags
         the tab dirty so close/evict can warn about unsaved changes. -->
    <div
      v-for="job in openJobs"
      :key="`pane-${job.api_id}`"
      v-show="activeJobId === job.api_id"
      class="gid-job-pane"
      @input="markJobTouched(job.api_id)"
      @change="markJobTouched(job.api_id)"
    >
      <job-summary-header
        :item="job.item"
        :job-contacts="jobsContacts[job.api_id]"
        :jmf-picklist="jmfPicklist"
        :locale="locale"
        :refreshing="!!refreshingJobs[job.api_id]"
        @refresh="refreshJobTab(job)"
        @close="closeJob(job.api_id)"
        @customer-click="handleCustomerClick"
        @partner-click="handlePartnerClick"
        @opportunity-click="handleOpportunityClick"
        @project-click="handleProjectClick"
        @call-initiated="showCommentModal"
        @view-quote="getQuotePath"
        @view-brand-quote="getBrandQuotePath"
      />
      <job-details
        :value="job.item"
        :job-contacts="jobsContacts[job.api_id]"
        @input="onJobSaved(job)"
        @comment-action="onJobSaved(job)"
        @open-job="openProjectJob"
      />
    </div>

    <partner-list ref="partnerView" />
    <log-list ref="logView" :statuses="statuses" :context="'admin'" />
    <add-comment-modal
      :visible="isCommentModalVisible"
      :selected-contact="selectedContact"
      @hide="isCommentModalVisible = false"
      @comment-added="handleCommentAdded"
      @close="isCommentModalVisible = false"
    />
    <b-modal
      v-model="isStatusGuideVisible"
      size="xl"
      scrollable
      ok-only
      :title="$t('job-status-guide.title')"
      :ok-title="$t('job-status-guide.close')"
      body-class="gid-status-guide__body"
    >
      <iframe
        v-if="isStatusGuideVisible"
        :src="statusGuideUrl"
        class="gid-status-guide__frame"
        :title="$t('job-status-guide.title')"
      />
      <template #modal-footer="{ ok }">
        <b-button
          variant="link"
          size="sm"
          :href="statusGuideUrl"
          target="_blank"
          rel="noopener"
        >
          {{ $t('job-status-guide.open-new-tab') }}
        </b-button>
        <b-button variant="primary" size="sm" @click="ok()">
          {{ $t('job-status-guide.close') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Api from '@gid/vue-common/api';
import axios from 'axios';
import { mapGetters, mapState } from 'vuex';
import { normalizeSearchInput } from '@gid/vue-common/utils';
import { appUrl } from '@gid/vue-common/app-settings/appUrls';
import FocusIndicator from '@gid/vue-common/components/FocusIndicator.vue';

import DaysSinceLastChange from '@gid/vue-common/components/DaysSinceLastChange';
import LogList from '@gid/vue-common/components/LogList';
import JobDetails from './JobDetails';
import JobPrice from './components/JobPrice';
import JobSummaryHeader from './components/JobSummaryHeader.vue';
import { openS4cSalesOrder } from '@/utils/dynamics';
import JobDocsDropdown from './components/JobDocsDropdown.vue';
import PartnerList from './components/PartnerList';

import AddCommentModal from '@/components/AddCommentModal.vue';
import CallContact from '@/components/CallContact.vue';
import { getOpportunityHint } from '@/utils/opportunity';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import {
  ContactProfileEnum,
  FormOrderModificationCategoryEnum,
  GeneralInvoicingTargetEnum,
  JobStatusEnum,
  JobTypeEnum,
  PartnerExperienceEnum,
  UserRoleEnum,
} from '@gid/models';
import { PrepaymentTypesEnum } from '@gid/models/dist/entities/opportunity-option';
import { StatusRulesNamesEnum } from '@gid/models/dist/entities/status-rules-names-enum';
import RatingWidget from '@gid/vue-common/components/RatingWidget.vue';
import { mapDetails } from '@gid/vue-common/components/activity/StateActivity.vue';
import AdvancedSearch from '@gid/vue-common/components/filters/AdvancedSearch.vue';
import QuickSearch from '@gid/vue-common/components/filters/QuickSearch.vue';
import InfiniteScrollMixin from '@gid/vue-common/components/mixins/InfiniteScrollMixin';
import {
  fetchContacts,
  jobContacts,
} from '@gid/vue-common/components/ticketing/interface/ticketing';
import { SET_JOB_FILTER } from '@gid/vue-common/store/jobs.module';
import {
  presetsByGroup,
  getPreset,
  presetToStoreFilter,
  presetCountRequests,
} from './jobPresets';
import { mapFilters } from '@gid/vue-common/store/utils';
import {
  _partnerExperienceLevel,
  getPartnerExperienceRange,
  isDKVMissingData,
} from '@gid/vue-common/utils';
import {
  BIconBox,
  BIconClipboard,
  BIconSliders,
  BIconThreeDots,
  BIconUpcScan,
  BIconCheckSquare,
  BIconLaptop,
  BIconWrench,
  BIconCart,
  BIconPower,
  BIconTools,
  BIconExclamationTriangle,
  BIconBootstrapReboot,
  BIconArrowRepeat,
  BIconArrowLeftRight,
  BIconSpeedometer2,
} from 'bootstrap-vue';
import JobTypes from '@gid/vue-common/components/JobTypes.vue';

// Static reference document served from public/ (see job-status-guide.html).
const STATUS_GUIDE_PATH = '/job-status-guide.html';

export default {
  mixins: [InfiniteScrollMixin],
  props: {
    job_id: String,
  },
  components: {
    BIconClipboard,
    JobDetails,
    JobPrice,
    JobSummaryHeader,
    JobDocsDropdown,
    LogList,
    PartnerList,
    RatingWidget,
    AdvancedSearch,
    DaysSinceLastChange,
    BIconBox,
    BIconUpcScan,
    BIconSliders,
    BIconThreeDots,
    CallContact,
    QuickSearch,
    JobTypes,
    AddCommentModal,
    FocusIndicator,
  },
  computed: {
    ...mapGetters(['locale', 'access_token']),
    ...mapState('jobPresets', {
      presetCounts: 'counts',
      presetRevenues: 'revenues',
    }),
    presetGroups() {
      return presetsByGroup();
    },
    activeView() {
      return this.$route.query.view || null;
    },
    activePresetLabel() {
      const preset = this.activeView ? getPreset(this.activeView) : null;
      return preset
        ? this.$t(`job-presets.titles.${preset.id}`)
        : this.$t('job-presets.all');
    },
    ...mapFilters({
      moduleName: 'jobs',
      states: [
        'search',
        'status',
        'owner',
        'test',
        'advancedSearch',
        'quickSearch',
        'useAdvancedSearch',
        'useQuickSearch',
      ],
      setMutation: SET_JOB_FILTER,
      setCallback: 'null',
    }),
    isJobPartiallyInvoiced() {
      return (item) => {
        const statusMapping = {
          invoiced: 'partial-invoiced-flag',
          scheduled: 'partial-scheduled-flag',
        };
        const statusKey = statusMapping[item?.is_invoicing_scheduled];
        return statusKey ? this.$t(statusKey) : null;
      };
    },
    partnerExperienceLevel() {
      return _partnerExperienceLevel;
    },
    copyToClipboard() {
      const buildJobURL = (_payload) => {
        let baseURL = window.location.href.substr(
          0,
          window.location.href.lastIndexOf('/'),
        );
        if (baseURL.indexOf('jobs') < 0) {
          baseURL = `${baseURL}/jobs`;
        }
        return `${baseURL}/${_payload}`;
      };

      return async (payload, constructURL = false) => {
        const performCopy = async (_payload) => {
          if (_payload && typeof _payload === 'string') {
            const clipStore = _payload;

            try {
              // Try modern clipboard API first
              if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(clipStore);
              } else {
                // Fallback for non-HTTPS environments
                const textArea = document.createElement('textarea');
                textArea.value = clipStore;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                  document.execCommand('copy');
                  textArea.remove();
                } catch (err) {
                  console.error('Failed to copy text:', err);
                  textArea.remove();
                  return;
                }
              }

              this.$bvToast.toast(`${clipStore} copied`, {
                title: 'Success',
                variant: 'success',
                solid: true,
                toaster: 'b-toaster-bottom-right',
              });
            } catch (err) {
              console.error('Failed to copy text:', err);
            }
          }
        };

        this.clicks++;
        if (this.clicks === 1) {
          // Copy immediately on the first click — no waiting for a double click.
          performCopy(payload);
          this.timer = setTimeout(() => {
            this.clicks = 0;
          }, 200);
        } else {
          // Second click within the window: value is already copied, so
          // additionally open the link in a new tab (where one makes sense).
          clearTimeout(this.timer);
          this.clicks = 0;
          if (constructURL && payload && typeof payload === 'string') {
            window.open(buildJobURL(payload), '_blank', 'noopener');
          }
        }
      };
    },
    handleOpportunityClick() {
      return (item) => {
        this.handleClickableText(
          'opportunity',
          item.opportunity.name,
          `/data-ui/Opportunity/${item.opportunity.sfid}`,
        );
      };
    },
    handlePartnerClick() {
      return (item) => {
        this.handleClickableText(
          'partner',
          item.partner.name,
          `/partners?search=${item.partner.id}&expandDetails=true`,
        );
      };
    },
    handleProjectClick() {
      return (item) => {
        this.handleClickableText(
          'project',
          item.project.name,
          `../jobs/${item.project.name}`,
        );
      };
    },
    handleCustomerClick() {
      return (item) => {
        this.handleClickableText(
          'customer',
          `${item.customer.first_name} ${item.customer.last_name}`,
          `./data-ui/Account/${item.customer.id}`,
        );
      };
    },
    showBrandQuoteLinks() {
      return (item) => {
        return item.opportunity.can_approve_proposal.includes(
          UserRoleEnum.BRAND,
        );
      };
    },
    showDaysSinceLastStatusChange() {
      // GID-2563: the job list defaults to sorting by the Ampel (days since last
      // status change) descending. The DB view only nulls that value for terminal
      // states (cancelled/closed/invoice sent/completed), so `new` and
      // `waiting for onboarding` jobs still carry a real days value and sort into
      // the list — hiding their badge made the column look unsorted (a stalled
      // `new` job appeared with no Ampel, only the blue actions count). Show the
      // badge for them so the displayed value matches the sort key. `completed`
      // stays hidden but is null in the view anyway, so it sinks to the bottom.
      return (item) => ![JobStatusEnum.COMPLETED].includes(item?.job?.status);
    },
    getProjectURL() {
      return (projectName) => {
        return `../jobs/${projectName}`;
      };
    },
    null() {
      return null;
    },
    getAlternativeLinks() {
      return (item) => item?.properties?.alternative_jobs;
    },
    tableFields() {
      return [
        'partner.name',
        'opportunity',
        'customer.shipping_address.postal_code',
        'customer._fullname',
        'job._price',
        'job.created_date',
        'job.appointment_start_timestamp',
        'days_since_last_status_change',
        'job.name',
        'actions',
      ].map((field) => this.fieldDefinitions[field]);
    },
    partnerRole() {
      return [GeneralInvoicingTargetEnum.PARTNER];
    },
    brandRole() {
      return [GeneralInvoicingTargetEnum.BRAND];
    },
    customerRole() {
      return [GeneralInvoicingTargetEnum.CUSTOMER];
    },
    isAlternative() {
      return (job) => {
        return job?.properties?.alternative_jobs?.length;
      };
    },
    hasAutoCreatedJob() {
      return (job) => {
        if (job?.job?.auto_created_job && job?.is_installation) {
          return {
            styles: { backgroundColor: '#7e52a0', color: 'white' },
            text: this.$t('job.has-auto-job-precheck'),
          };
        } else if (
          job?.job?.auto_created_job &&
          job?.is_activation &&
          job?.job?.status === JobStatusEnum.NEW
        ) {
          return {
            styles: { backgroundColor: '#647421', color: 'white' },
            text: this.$t('job.has-auto-job-installation'),
          };
        }
      };
    },
    hasNextStepJob() {
      return (job) => {
        if (job?.job?.next_step_job && job?.is_precheck) {
          return {
            styles: { backgroundColor: '#7e52a0', color: 'white' },
            text: this.$t('job.has-next-step-job-precheck'),
          };
        } else if (job?.job?.next_step_job && job?.is_installation) {
          return {
            styles: { backgroundColor: '#647421', color: 'white' },
            text: this.$t('job.has-next-step-job-installation'),
          };
        }
      };
    },
    hasOwnInstaller() {
      return (job) => {
        return job?.job?.own_installer;
      };
    },
    isReclamation() {
      return (job) => {
        if (job?.job?.is_reclamation) {
          return {
            styles: { backgroundColor: '#b4532a', color: 'white' },
            text: this.$t('job.is-reclamation'),
          };
        }
      };
    },
    shouldDisplayInternalProtocol() {
      return (job) => {
        return [
          JobStatusEnum.APPROVAL_PENDING,
          JobStatusEnum.COMPLETED,
          JobStatusEnum.INVOICE_SENT,
          JobStatusEnum.FINISHED,
        ].includes(job?.status);
      };
    },
    shouldDisplayWallboxScore() {
      return (item) => {
        const notInStatuses = [
          JobStatusEnum.CLOSED,
          JobStatusEnum.APPROVAL_PENDING,
          JobStatusEnum.FINISHED,
          JobStatusEnum.CANCELLED,
          JobStatusEnum.INVOICE_SENT,
          JobStatusEnum.COMPLETED,
        ];
        if (
          item?.productOrder &&
          item.productOrder.status != 'all' &&
          !notInStatuses.includes(item.job.status)
        ) {
          if (!item?.is_precheck && !item?.is_activation) {
            return true;
          }
        }

        return false;
      };
    },
    getCarTypeExist() {
      return (item) => item.properties?.car_type?.length;
    },
    getCarChargingTypeExist() {
      return (item) => item.properties?.charging_opportunity?.length;
    },
    getUrgentReason() {
      return (item) => item.properties?.urgent_reason;
    },
    getChargingType() {
      return (charging_possibility) => {
        let humanizedType = '';
        if (charging_possibility == 'Ja') {
          humanizedType = this.$t('yes');
        }
        if (charging_possibility == 'Nein') {
          humanizedType = this.$t('no');
        }
        return humanizedType;
      };
    },
    getCarType() {
      return (car_type) => {
        let humanizedType = '';
        if (car_type == 'Voll elektrisches Auto')
          humanizedType = this.$t('car_type.electric');
        if (car_type == 'Hybrid-Auto')
          humanizedType = this.$t('car_type.hybrid');
        return humanizedType;
      };
    },
  },
  data() {
    return {
      // Pending single-click copy on the S4C chip. A dblclick fires two clicks
      // first, so the copy is deferred long enough for onS4cDblClick to cancel
      // it — otherwise opening Dynamics also toasts "copied" twice.
      s4cClickTimer: null,
      // Job console tabs — up to 5 open jobs; `activeJobId === null` = List tab.
      openJobs: [],
      activeJobId: null,
      maxOpenJobs: 5,
      // api_id -> true while that tab's data is being re-fetched (refresh button).
      refreshingJobs: {},
      // Per-tab window scroll position (key: 'list' or a job's api_id) so each
      // tab keeps its own scroll — scrolling a job detail never moves the list.
      scrollPositions: {},
      clicks: 0,
      timer: {},
      clickState: {
        opportunity: { clicks: 0, timer: null },
        partner: { clicks: 0, timer: null },
        project: { clicks: 0, timer: null },
        customer: { clicks: 0, timer: null },
      },
      jobRatings: {},
      jobsContacts: {},
      fieldDefinitions: {
        opportunity: {
          label: this.$t('job.table.heading.opportunity'),
          key: 'opportunity',
        },
        'customer.shipping_address.postal_code': {
          label: this.$t('job.table.heading.zip'),
          key: 'customer.shipping_address.postal_code',
        },
        'customer._fullname': {
          label: this.$t('job.table.heading.customer'),
          key: 'customer._fullname',
        },
        'job._price': {
          label: this.$t('job.table.heading.price'),
          key: 'job._price',
          class: 'text-right',
        },
        'job.name': {
          label: this.$t('job.table.heading.job'),
          key: 'job.name',
        },
        actions: {
          label: this.$t('job.table.heading.actions'),
          key: 'actions',
        },
        'partner.name': {
          label: this.$t('job.table.heading.partner'),
          key: 'partner.name',
          sortable: true,
        },
        'job.created_date': {
          label: this.$t('job.table.heading.created_date'),
          key: 'job.created_date',
          sortable: true,
        },
        'job.appointment_start_timestamp': {
          label: this.$t('job.table.heading.appointment'),
          key: 'job.appointment_start_timestamp',
          sortable: true,
          formatter: (value) => (value ? this.$moment(value).format('L') : '-'),
        },
        days_since_last_status_change: {
          label: this.$t('job.table.heading.ampel'),
          key: 'days_since_last_status_change',
          sortable: true,
          // GID-2563: widen the Ampel column ~50% (was ~120px auto) for the badges.
          thStyle: { width: '180px', minWidth: '180px' },
        },
      },
      advancedFilterOptions: [
        {
          label: this.$i18n.t('filters.created-date'),
          type: 'createddate',
          component: 'from-to-filter',
        },
        {
          label: this.$i18n.t('filters.installation-date'),
          type: 'appointment_date',
          component: 'from-to-filter',
        },
        {
          label: this.$i18n.t('filters.completed-date'),
          type: 'completed_date',
          component: 'from-to-filter',
        },
        {
          label: this.$i18n.t('filters.car_delivery'),
          type: 'car_delivery_date',
          component: 'from-to-filter',
        },
        {
          label: 'Car type',
          type: 'car_type',
          component: 'filter-multiselect',
          values: [
            { text: 'Voll elektrisches Auto', value: 'electric' },
            { text: 'Hybrid-Auto', value: 'hybrid' },
          ],
        },
        {
          label: 'Charging possibility',
          type: 'charging_opportunity',
          component: 'filter-multiselect',
          values: [
            { text: 'Ja', value: 'yes' },
            { text: 'Nein', value: 'no' },
          ],
        },
        {
          label: 'Override reason',
          type: 'urgent_reasons',
          component: 'filter-multiselect',
          values: [
            { text: 'Dringend vorzuziehen', value: 'urgent' },
            { text: 'Nein', value: 'no' },
          ],
        },
        {
          label: this.$i18n.t('filters.external-object-id'),
          type: 'eid',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.external-invoicing-id'),
          type: 'invoicing_eid',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.external-partner-eid'),
          type: 'partner_eid',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.s4c-order-id'),
          type: 's4c_order_id',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.job-type'),
          type: 'job_type',
          component: 'filter-multiselect',
          values: [
            { text: 'Precheck', value: JobTypeEnum.PRECHECK },
            { text: 'Installation', value: JobTypeEnum.INSTALLATION },
            { text: 'Activation', value: JobTypeEnum.ACTIVATION },
            { text: 'Hardware', value: JobTypeEnum.HARDWARE_SALE },
            { text: 'Remote', value: JobTypeEnum.REMOTE },
            {
              text: 'Defect Elimination',
              value: JobTypeEnum.DEFECT_ELIMINATION,
            },
            { text: 'Maintenance', value: JobTypeEnum.MAINTENANCE },
            { text: 'Meter Application', value: JobTypeEnum.METER_APPLICATION },
            {
              text: 'Installation Preparation',
              value: JobTypeEnum.INSTALLATION_PREPARATION,
            },
            {
              text: 'Reclamation',
              value: 'reclamation',
            },
            { text: 'Replacement', value: JobTypeEnum.REPLACEMENT },
            { text: 'Migration', value: JobTypeEnum.MIGRATION },
          ],
        },
        {
          label: this.$i18n.t('inputs.headings.affiliate_id'),
          type: 'affiliate_id',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/affiliate-id',
          searchFields: ['id', 'affiliateId'],
          trackBy: 'affiliateId',
          selectLabel: 'affiliateId',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.partner'),
          type: 'service_partner',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/partner',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.parent-partner'),
          type: 'parent_partner',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/partner',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
          queryParams: {
            search: {
              'childAccounts.id': { $notnull: true },
            },
          },
        },
        {
          label: this.$i18n.t('filters.opportunity'),
          type: 'opportunity',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/opportunity',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.product'),
          type: 'products',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/product',
          searchFields: ['name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.tracking-id'),
          type: 'tracking_id',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.comment'),
          type: 'comment',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.product-serial-number'),
          type: 'product_serial_number',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.brand'),
          type: 'brand',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/brand',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          valueOfinitFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.negative-margin'),
          type: 'negative_margin',
          component: 'b-form-checkbox',
        },
        {
          label: this.$i18n.t('filters.partially-invoiced'),
          type: 'partially_invoicing_scheduled',
          component: 'b-form-checkbox',
        },
        {
          label: this.$i18n.t('filters.automatically-completed'),
          type: 'automatically_completed',
          component: 'b-form-checkbox',
        },
        {
          label: this.$i18n.t('filters.payment-reminder-blocked'),
          type: 'payment_reminder_blocker',
          component: 'b-form-checkbox',
        },
        {
          label: this.$i18n.t('filters.invoices-sent'),
          type: 'invoices_sent',
          component: 'b-form-checkbox',
        },
        {
          label: this.$i18n.t('filters.automatically-created'),
          type: 'automatically-created',
          component: 'b-form-checkbox',
        },
        {
          label: this.$i18n.t('filters.service'),
          type: 'service',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/service',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'descriptionDe',
          customLabel: (item) => {
            return `[${item.opportunity.name}] ${item.descriptionDe}`;
          },
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.state'),
          type: 'state',
          component: 'filter-multiselect',
          values: [
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.MISSING_INVOICING_EID}.missing`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.MISSING_EID}.missing`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_PREWORK}.awaiting`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.HOME_ELECTRICIAN_NOT_REACHABLE}.unreachable`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_NOT_REACHABLE}.unreachable`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_WAITING_PRODUCT}.awaiting`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_WAITING_SUBSIDY}.awaiting`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.CUSTOMER_PAUSE_GENERIC}.paused`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PRODUCT_ORDERING}.pending-order`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PRODUCT_ORDERING}.pending-delivery`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.BRAND_PRODUCT_ORDERING}.waiting`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PARTNER_INFO}.awaiting`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PARTNER_PAUSE_GENERIC}.paused`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.BRAND_PAUSE_GENERIC}.paused`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.PARTNER_ONBOARDING}.pending`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.ADMIN_VALIDATION}.pending`,
            `${JobStatusEnum.STARTED}.${StatusRulesNamesEnum.CUSTOMER_SIGNATURE}.pending`,
            `${JobStatusEnum.STARTED}.${StatusRulesNamesEnum.CUSTOMER_SIGNATURE}.missing-customer`,
            `${JobStatusEnum.STARTED}.${StatusRulesNamesEnum.PROTOCOL_FORM}.pending`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.PARTNER_QUOTE_VALIDATION}.pending-validation`,
            `${JobStatusEnum.ON_HOLD}.${StatusRulesNamesEnum.NETWORK_OPERATOR}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.AGREEMENT_OWNER}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.CUSTOMER_PREWORK_BEFORE_ACCEPTED}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.CUSTOMER_INFO}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.CUSTOMER_INFO_GENERIC}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.BRAND_INFO_GENERIC}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.PARTNER_INFO_GENERIC}.waiting`,
            `${JobStatusEnum.ON_HOLD_BEFORE_ACCEPTED}.${StatusRulesNamesEnum.ADMIN_VALIDATION}.pending`,
          ]
            .map((value) => ({
              text: this.$t(`state.${value}`),
              value,
            }))
            .sort((a, b) => a.text.localeCompare(b.text)),
        },
        {
          label: this.$i18n.t('filters.partner-experience'),
          type: 'partner-experience',
          component: 'filter-multiselect',
          values: Object.values(PartnerExperienceEnum).map((value) => {
            const [lowerRange, upperRange] = getPartnerExperienceRange(value);
            return {
              text: value,
              value: JSON.stringify({
                [`partner-experience_from`]: lowerRange,
                [`partner-experience_to`]: upperRange,
              }),
            };
          }),
          multiple: false,
        },
      ].sort((a, b) => a.label.localeCompare(b.label)),
      filterCombinations: [
        {
          text: this.$t('filters.precheck'),
          icon: BIconCheckSquare,
          type: 'job_type',
          value: JobTypeEnum.PRECHECK,
        },
        {
          text: this.$t('filters.remote-precheck'),
          icon: BIconLaptop,
          type: 'job_type',
          value: 'rpr',
        },
        {
          text: this.$t('filters.onsite-precheck'),
          icon: BIconCheckSquare,
          type: 'job_type',
          value: 'onsitepr',
        },
        {
          text: this.$t('filters.installation'),
          icon: BIconWrench,
          type: 'job_type',
          value: JobTypeEnum.INSTALLATION,
        },
        {
          text: this.$t('filters.hardware'),
          icon: BIconCart,
          type: 'job_type',
          value: JobTypeEnum.HARDWARE_SALE,
        },
        {
          text: this.$t('filters.activation'),
          icon: BIconPower,
          type: 'job_type',
          value: JobTypeEnum.ACTIVATION,
        },
        {
          text: this.$t('remote-job'),
          icon: BIconLaptop,
          type: 'job_type',
          value: JobTypeEnum.REMOTE,
        },
        {
          text: this.$t('filters.maintenance'),
          icon: BIconTools,
          type: 'job_type',
          value: JobTypeEnum.MAINTENANCE,
        },
        {
          text: this.$t('filters.defect-elimination'),
          icon: BIconExclamationTriangle,
          type: 'job_type',
          value: JobTypeEnum.DEFECT_ELIMINATION,
        },
        {
          text: this.$t('filters.reclamation'),
          icon: BIconBootstrapReboot,
          type: 'job_type',
          value: 'reclamation',
        },
        {
          text: this.$t('filters.replacement'),
          icon: BIconArrowRepeat,
          type: 'job_type',
          value: JobTypeEnum.REPLACEMENT,
        },
        {
          text: this.$t('filters.migration'),
          icon: BIconArrowLeftRight,
          type: 'job_type',
          value: JobTypeEnum.MIGRATION,
        },
      ],
      perPage: 10,
      totalRows: null,
      // Count + revenue + cost for the current filter set (POST
      // /api/admin/jobs/totals). null while in flight or after a failure, which
      // renders as no figures at all rather than a misleading zero.
      totals: null,
      // Own auto-cancel key, kept out of `data` reactivity concerns: it is only
      // ever used as a Map identity by the Api helper.
      totalsCancelScope: Object.freeze({ scope: 'jobs-totals' }),
      // Same idea for the single-job lookup behind a deep link / project link.
      jobLookupCancelScope: Object.freeze({ scope: 'jobs-lookup' }),
      // GID-2563: default-sort the job list by Ampel/urgency (days since last status
      // change) descending so the reddest, most-stalled jobs surface at the top.
      sortBy: 'days_since_last_status_change',
      sortDir: true,
      statuses: [],
      owners: [],
      error: null,
      productOrderStatus: {},
      items: [],
      loading: 0,
      isCommentModalVisible: false,
      isStatusGuideVisible: false,
      statusGuideUrl: STATUS_GUIDE_PATH,
      currentJobApiId: null,
      selectedContact: null,
      jmfPicklist: [],
    };
  },
  methods: {
    onS4cClick(item) {
      clearTimeout(this.s4cClickTimer);
      this.s4cClickTimer = setTimeout(() => {
        this.copyToClipboard(item.job.s4c_order_id, false);
      }, 250);
    },
    onS4cDblClick(item) {
      clearTimeout(this.s4cClickTimer);
      openS4cSalesOrder(item);
    },
    extractJobName(cardElement) {
      // Check if this is a detail row (b-table-details)
      if (cardElement.classList.contains('b-table-details')) {
        // For detail rows, we need to find the job name from the PARENT job row
        // The structure is: [job row] -> [d-none row] -> [detail row]
        // So we need to skip d-none rows and find the actual job row with the job link
        let prevRow = cardElement.previousElementSibling;
        while (prevRow) {
          // Skip d-none (hidden) rows and other detail rows
          if (
            prevRow.classList.contains('d-none') ||
            prevRow.classList.contains('b-table-details')
          ) {
            prevRow = prevRow.previousElementSibling;
            continue;
          }
          // This should be the parent job row - try to extract job name
          // Try data-label approach (mobile/stacked mode)
          let jobNameLink = prevRow.querySelector(
            'td[data-label="Job Nummer"] a',
          );
          if (jobNameLink) {
            return jobNameLink.textContent?.trim() || null;
          }
          // Try direct link approach (desktop mode) - look for job links
          const jobLinks = prevRow.querySelectorAll('a[href*="/jobs/O-"]');
          if (jobLinks.length > 0) {
            return jobLinks[0].textContent?.trim() || null;
          }
          // If this row doesn't have the job link, keep looking
          prevRow = prevRow.previousElementSibling;
        }
        // Fallback: try to find job links in the detail row itself
        // But filter to only get actual job numbers (O-xxxxx pattern)
        const jobLinks = cardElement.querySelectorAll('a[href*="/jobs/O-"]');
        if (jobLinks.length > 0) {
          return jobLinks[0].textContent?.trim() || null;
        }
        return null;
      }
      // For regular job rows, extract from Job Nummer cell
      // Try data-label approach (mobile/stacked mode)
      let jobNameLink = cardElement.querySelector(
        'td[data-label="Job Nummer"] a',
      );
      if (jobNameLink) {
        return jobNameLink.textContent?.trim() || null;
      }
      // Try direct link approach (desktop mode)
      const jobLinks = cardElement.querySelectorAll('a[href*="/jobs/O-"]');
      if (jobLinks.length > 0) {
        return jobLinks[0].textContent?.trim() || null;
      }
      return null;
    },
    async copyToClipboardWithToast(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            document.execCommand('copy');
          } finally {
            textArea.remove();
          }
        }
        this.$bvToast.toast(`${text} copied`, {
          title: 'Success',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right',
        });
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    },
    handleClickableText(key, textToCopy, urlToOpen) {
      const state = this.clickState[key];
      state.clicks++;
      if (state.clicks === 1) {
        state.timer = setTimeout(() => {
          this.copyToClipboardWithToast(textToCopy);
          state.clicks = 0;
        }, 200);
      } else {
        clearTimeout(state.timer);
        window.open(urlToOpen, '_blank');
        state.clicks = 0;
      }
    },
    formatCustomerTitle(title, salutation) {
      const formatedTitle = (title?.split(' ') || []).map((t) =>
        t.toLowerCase(),
      );
      const formatedSalutation = (salutation?.split(' ') || []).map((t) =>
        t.toLowerCase(),
      );
      const combined = [...new Set([...formatedTitle, ...formatedSalutation])]
        .filter((t) => t !== 'herr' && t !== 'frau')
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1));
      return combined.join(' ');
    },
    async getInternalProtocolPath(job_id) {
      const internalProcotolTab = window.open('', '_blank');
      internalProcotolTab.document.title = 'Loading Internal Protocol...';
      const resp = await axios.get(
        `/api/admin/jobs/${job_id}/internal-protocol`,
      );
      const path = `/api/media/file/${resp.data}?jwt=${this.access_token}`;
      internalProcotolTab.location.href = path;
    },
    async getQuotePath(job_id) {
      const resp = await axios.get(`/api/admin/jobs/${job_id}/quote`);
      const path = `/api/media/file/${resp.data}?jwt=${this.access_token}`;
      window.open(path, '_blank').focus();
    },
    async getBrandQuotePath(job_id) {
      const resp = await axios.get(`/api/admin/jobs/${job_id}/brand-quote`);
      const path = `/api/media/file/${resp.data}?jwt=${this.access_token}`;
      window.open(path, '_blank').focus();
    },
    // The guide is a static page, so it cannot read the app's dark-mode store.
    // Hand the current theme down as a query param; the page falls back to
    // prefers-color-scheme when the param is absent.
    openStatusGuide() {
      const dark =
        document.documentElement.classList.contains('dark-mode') ||
        document.body.classList.contains('dark-mode');
      this.statusGuideUrl = `${STATUS_GUIDE_PATH}?theme=${
        dark ? 'dark' : 'light'
      }`;
      this.isStatusGuideVisible = true;
    },
    showCommentModal({ jobApiId, selectedContact }) {
      this.currentJobApiId = jobApiId;
      this.isCommentModalVisible = true;
      this.selectedContact = selectedContact;
    },
    handleCommentAdded(comment) {
      this.createComment(this.currentJobApiId, comment);
      this.isCommentModalVisible = false;
    },
    createComment(jobApiId, message) {
      const commentData = {
        actionType: 'phone',
        jobApiId,
        message,
        visibleForRoles: [
          UserRoleEnum.ADMIN,
          UserRoleEnum.BRAND,
          UserRoleEnum.CUSTOMER,
          UserRoleEnum.PARTNER,
        ],
      };
      axios.post(`/data-api/comment`, commentData);
    },
    // ---- Job console tabs --------------------------------------------------
    openJobTab(item) {
      if (!item || !item.api_id) return;
      const id = item.api_id;
      const existing = this.openJobs.find((j) => j.api_id === id);
      if (existing) {
        existing.item = item;
        this.activateJob(id);
        return;
      }
      if (this.openJobs.length >= this.maxOpenJobs) {
        // Evict the least-recently-viewed CLEAN tab; only fall back to a dirty
        // one (with a confirm) if every open tab has unsaved changes.
        const pool = this.openJobs.filter((j) => !j.touched);
        const candidates = pool.length ? pool : this.openJobs;
        const victim = candidates.reduce((a, b) =>
          a.lastViewed <= b.lastViewed ? a : b,
        );
        if (
          victim.touched &&
          !window.confirm(
            this.$t('job.console.confirm-evict', { job: victim.jobNo }),
          )
        ) {
          return;
        }
        this.removeJob(victim.api_id);
      }
      this.openJobs.push({
        api_id: id,
        jobNo: (item.job && item.job.name) || id,
        customer: item.customer
          ? item.customer.last_name || item.customer.first_name || ''
          : '',
        item,
        touched: false,
        lastViewed: Date.now(),
      });
      this.activateJob(id);
    },
    // A job link inside the project-flow strip was clicked: open (or focus) the
    // job as another console tab instead of a new browser tab.
    async openProjectJob(projectItem) {
      const name = projectItem?.job?.name;
      if (!name) return;
      // Already open? Just focus its tab (openJobs holds full job-view items).
      const existing = this.openJobs.find(
        (j) => j.item && j.item.job && j.item.job.sfid === projectItem.sfid,
      );
      if (existing) {
        this.activateJob(existing.api_id);
        return;
      }
      // The project strip carries a reduced job shape; the full job-view is
      // fetched by name, same as a deep link.
      await this.openJobByName(name);
    },
    // Open a job as an active console tab from its job number alone — used by
    // the `/jobs/<job-name>` deep link and by the project-flow strip, neither of
    // which has the full job-view the list rows carry.
    async openJobByName(name) {
      if (!name) return;
      const existing = this.openJobs.find((j) => j.jobNo === name);
      if (existing) {
        this.activateJob(existing.api_id);
        return;
      }
      try {
        // Same endpoint + use_table_fields as the list, so the tab and its
        // summary header render identically to one opened from a row. Its own
        // auto-cancel scope: `loadData` cancels on `this`, and sharing the key
        // would make the list load and this lookup kill each other.
        const response = await Api.post(
          `/api/admin/jobs`,
          {
            use_table_fields: true,
            page_size: 1,
            page_number: 0,
            search: normalizeSearchInput(name),
            include_test_jobs: true,
          },
          { autoCancelScope: this.jobLookupCancelScope },
        );
        const item = response.data?.data?.[0];
        if (!item) {
          this.$bvToast.toast(this.$t('job.console.not-found', { job: name }), {
            title: this.$t('_errors.server.title'),
            variant: 'warning',
          });
          return;
        }
        this.openJobTab(item);
        // Contacts power the call buttons in the tab's summary header; a row
        // opened from the list gets them via loadRowExtras, this one doesn't.
        this.loadJobsContacts([item]);
      } catch (error) {
        this.$bvToast.toast(
          error.response?.status || this.$t('_errors.network.details'),
          {
            title: this.$t('_errors.server.title'),
            variant: 'danger',
          },
        );
      }
    },
    // Refresh button on an open job tab: re-fetch this job's full job-view
    // (same POST endpoint + use_table_fields the list uses, so the summary
    // header keeps every table-computed field) and swap it into the tab in
    // place. Only this tab's data + summary + details update; others untouched.
    async refreshJobTab(job) {
      if (!job || !job.api_id) return;
      const id = job.api_id;
      const name = job.item?.job?.name;
      if (!name) return;
      this.$set(this.refreshingJobs, id, true);
      try {
        const response = await Api.post(
          `/api/admin/jobs`,
          {
            use_table_fields: true,
            page_size: 1,
            page_number: 0,
            search: normalizeSearchInput(name),
            include_test_jobs: true,
          },
          { autoCancelScope: this },
        );
        const item = response.data?.data?.[0];
        if (!item) {
          this.$bvToast.toast(this.$t('job.console.refresh-failed'), {
            title: this.$t('_errors.server.title'),
            variant: 'danger',
          });
          return;
        }
        // Swap fresh data into the tab; JobDetails watches `value` and reloads.
        job.item = item;
        // Keep the underlying list row in sync if it's currently loaded.
        this.items = this.items.map((row) =>
          row.job && item.job && row.job.sfid === item.job.sfid ? item : row,
        );
        // Refresh only this job's contacts entry (don't clobber other tabs').
        try {
          const accounts = [
            ...new Set([item.brand?.id, item.customer?.id, item.partner?.id]),
          ];
          const contacts = await fetchContacts(accounts);
          const mapped = jobContacts(contacts, item);
          // fetchContacts swallows transient errors/cancellations into an empty
          // list, so an empty result here is ambiguous. Only overwrite when we
          // actually got contacts (or the job truly has no accounts to look up);
          // otherwise keep the existing entry so the call buttons don't vanish
          // on a flaky refresh.
          if (mapped.length || !accounts.some(Boolean)) {
            this.$set(this.jobsContacts, item.api_id, mapped);
          }
        } catch {
          // Contacts are best-effort; the job data already refreshed.
        }
      } catch (error) {
        this.$bvToast.toast(
          error.response?.status || this.$t('_errors.network.details'),
          {
            title: this.$t('_errors.server.title'),
            variant: 'danger',
          },
        );
      } finally {
        this.$set(this.refreshingJobs, id, false);
      }
    },
    currentTabKey() {
      return this.activeJobId === null ? 'list' : this.activeJobId;
    },
    getScrollTop() {
      return (
        (document.scrollingElement || document.documentElement).scrollTop ||
        window.pageYOffset ||
        0
      );
    },
    setScrollTop(y) {
      const el = document.scrollingElement || document.documentElement;
      if (el) el.scrollTop = y;
      window.scrollTo(0, y);
    },
    saveScroll() {
      this.scrollPositions[this.currentTabKey()] = this.getScrollTop();
    },
    restoreScroll(key) {
      const y = this.scrollPositions[key] || 0;
      // wait for v-show to reveal the target pane (so its height exists) before
      // restoring — otherwise the scroll would clamp to the hidden pane's height
      this.$nextTick(() => {
        this.setScrollTop(y);
        // second pass after layout settles (async detail content, tables)
        requestAnimationFrame(() => this.setScrollTop(y));
      });
    },
    switchTab(key) {
      if (this.currentTabKey() === key) return;
      this.saveScroll();
      this.activeJobId = key === 'list' ? null : key;
      if (this.activeJobId !== null) {
        const job = this.openJobs.find((j) => j.api_id === this.activeJobId);
        if (job) job.lastViewed = Date.now();
      }
      this.restoreScroll(key);
    },
    activateJob(id) {
      this.switchTab(id);
    },
    markJobTouched(id) {
      const job = this.openJobs.find((j) => j.api_id === id);
      if (job) job.touched = true;
    },
    closeJob(id) {
      const job = this.openJobs.find((j) => j.api_id === id);
      if (
        job &&
        job.touched &&
        !window.confirm(
          this.$t('job.console.confirm-close', { job: job.jobNo }),
        )
      ) {
        return;
      }
      this.removeJob(id);
    },
    removeJob(id) {
      const idx = this.openJobs.findIndex((j) => j.api_id === id);
      if (idx === -1) return;
      this.openJobs.splice(idx, 1);
      delete this.scrollPositions[id];
      if (this.activeJobId === id) {
        // fall back to the most-recently-viewed remaining job, else the List
        const next = this.openJobs
          .slice()
          .sort((a, b) => b.lastViewed - a.lastViewed)[0];
        this.activeJobId = next ? next.api_id : null;
        this.restoreScroll(this.currentTabKey());
      }
    },
    onJobSaved(job) {
      job.touched = false;
      this.refreshJob(job.item);
    },
    async refreshJob(job_view) {
      try {
        const { data: job } = await axios.get(
          `/api/admin/jobs/${job_view.job.sfid}`,
        );
        this.jobUpdated(job);
      } catch (error) {
        this.$bvToast.toast(
          error.response?.status || this.$t('_errors.network.details'),
          {
            title: this.$t('_errors.server.title'),
            variant: 'danger',
          },
        );
      }
    },
    jobUpdated(newJob) {
      this.items = this.items.map((item) =>
        item.job.sfid == newJob.job.sfid ? { ...newJob } : item,
      );
      // keep an open console tab pointed at the freshly-loaded job data
      const tab = this.openJobs.find(
        (j) => j.item && j.item.job && j.item.job.sfid === newJob.job.sfid,
      );
      if (tab) {
        tab.item = { ...newJob };
      }
    },
    // The filter half of the list request — everything that selects *which*
    // jobs, and nothing about paging, sorting or which rows are already loaded.
    // Shared with `loadTotals()` so the count / revenue / cost figures always
    // describe exactly the rows the table is showing.
    currentFilterParams() {
      const params = {
        search: normalizeSearchInput(this.filterSearch),
        status: this.filterStatus?.map((s) => s.value),
        owner: this.filterOwner,
        include_test_jobs: this.filterTest,
      };
      if (this.filterUseAdvancedSearch || this.filterUseQuickSearch) {
        let filters = {};
        if (this.filterUseAdvancedSearch && this.filterAdvancedSearch) {
          filters = {
            ...JSON.parse(JSON.stringify(this.filterAdvancedSearch)),
          };
        }
        if (this.filterUseQuickSearch && this.filterQuickSearch) {
          const logic = Object.keys(filters)[0];
          if (logic && this.filterQuickSearch[logic]) {
            filters[logic].push(...this.filterQuickSearch[logic]);
          } else {
            filters = this.filterQuickSearch;
          }
        }
        params.search_json = JSON.stringify(filters);
      }
      return params;
    },
    // Revenue/cost sums cost seconds on wide filter sets, so this runs only when
    // the filter set changes — never on a page append or a re-sort, which return
    // the same rows and therefore the same totals.
    async loadTotals() {
      this.totals = null;
      try {
        const { data } = await Api.post(
          '/api/admin/jobs/totals',
          this.currentFilterParams(),
          // A scope of its own: `loadData` auto-cancels on `this`, and sharing
          // the key would make the list and the totals cancel each other.
          { autoCancelScope: this.totalsCancelScope },
        );
        this.totals = data;
      } catch (error) {
        // Decorative: a failed total must never disturb the list itself, so it
        // just stays hidden. (An auto-cancelled request never settles, so a
        // superseded filter change can't land here either.)
        this.totals = null;
      }
    },
    async loadData(append = false) {
      if (!append) {
        this.totalRows = null;
        this.items = [];
        // The row-extra maps are keyed by job, so they have to be dropped
        // alongside the rows they describe — an append merges into them.
        this.jobRatings = {};
        this.jobsContacts = {};
        this.loadTotals();
      }
      if (this.totalRows !== null && this.items.length >= this.totalRows) {
        return;
      }
      this.loading = (this.totalRows ?? this.perPage) - this.items.length;
      if (this.loading > this.perPage) {
        this.loading = this.perPage;
      }
      let sort_by = this.sortBy ? this.sortBy : null;

      let params = {
        ...this.currentFilterParams(),
        use_table_fields: true,
        page_size: this.perPage,
        page_number: 0,
        sort_by,
        sort_dir: this.sortDir ? 'desc' : 'asc',
        excluding: this.items.map(({ api_id }) => api_id).filter(Boolean),
      };

      Api.post(`/api/admin/jobs`, params, {
        autoCancelScope: this,
      })
        .then((response) => {
          this.error = null;
          this.totalRows =
            this.items.length + response.data.pagination.total_count;
          const newRows = response.data.data;
          this.items.push(...newRows);
          this.loadRowExtras(newRows);
        })
        .catch((error) => {
          const err = { details: error };
          if (error.response) {
            err.message = error.response.data.msg || error.response.data;
          } else if (error.request) {
            err.message = this.$t('_errors.network.details');
          } else {
            err.message = error.message;
          }
          this.error = err;
        })
        .finally(() => {
          this.loading = 0;
        });
    },
    greaterDateFormatted(a, b) {
      const date = a && b ? (a > b ? a : b) : a ? a : b;
      return date
        ? this.$t('job.readyforinstallation', {
            installationdate: this.$moment(date).format('L'),
          })
        : '-';
    },
    latestStatusChangeFormatted(item) {
      return this.$t('job.statuschange', {
        status_change_date: this.$moment(
          item.job.last_status_change ?? item.job.created_date,
        ).format('L'),
      });
    },
    isPrepayment(job_view) {
      return [
        PrepaymentTypesEnum.REQUIRED,
        PrepaymentTypesEnum.OPTIONAL,
      ].includes(job_view.opportunity.prepayment);
    },
    isDKVMissingData(job_view) {
      return isDKVMissingData(job_view);
    },
    hasInstallationIncidentRaisedByPartner(job_view) {
      return (
        job_view.is_installation &&
        job_view.has_installation_incident_raised_by_partner
      );
    },
    // Everything a row needs beyond the list payload itself. Called once per
    // appended page with only that page's rows: two of these loaders issue one
    // request per job, so running them over the whole accumulated list on every
    // append made scrolling quadratic — appending 10 rows to a 150-row list
    // fired ~190 requests and blocked the main thread for seconds.
    loadRowExtras(rows) {
      if (!rows?.length) return;
      this.loadJobRatings(rows);
      this.loadProductOrders(rows);
      this.loadJobsContacts(rows);
      this.loadInstallationIncidentFlags(rows);
      this.loadNumPartnersOfferIsVisibleTo(rows);
    },
    async loadJobRatings(items) {
      if (!items?.length) return;
      try {
        const response = await axios.post(
          '/rating-api/averages/for-admin/job',
          {
            ids: items.map((entry) => entry.api_id),
          },
        );
        this.jobRatings = { ...this.jobRatings, ...response.data };
      } catch {
        // Ignore error
      }
    },
    async loadJobsContacts(items) {
      if (!items?.length) return;
      const allAccounts = items.reduce((all, i) => {
        all.push(i.brand?.id, i.customer?.id, i.partner?.id);
        return all;
      }, []);
      const contacts = await fetchContacts([...new Set(allAccounts)]);
      const next = { ...this.jobsContacts };
      items.forEach((i) => {
        next[i.api_id] = jobContacts(contacts, i);
      });
      this.jobsContacts = next;
    },
    async loadInstallationIncidentFlags(rows = this.items) {
      const jobs = rows.filter((job_view) => job_view.is_installation);
      if (jobs.length == 0) {
        return;
      }
      // Hold the job objects rather than looking them up again per response:
      // a findIndex per settled request is O(n²) over a long list, and writing
      // the rows one at a time re-rendered the whole table once per job.
      const results = await Promise.all(
        jobs.map((job) =>
          axios
            .get(
              `/order-intake-forms-api/form/has-incident/${job.api_id}/category/${FormOrderModificationCategoryEnum.DOCUMENTATION}`,
            )
            .then(({ data }) => ({ job, incident: data.incident }))
            .catch(() => null),
        ),
      );
      results.filter(Boolean).forEach(({ job, incident }) => {
        this.$set(job, 'installation', { incident });
      });
    },
    async loadNumPartnersOfferIsVisibleTo(rows = this.items) {
      const jobs = rows.filter(
        (job_view) =>
          [
            JobStatusEnum.NEW,
            JobStatusEnum.PROPOSAL_APPROVAL_PENDING,
            JobStatusEnum.OPEN,
            JobStatusEnum.ACCEPTED,
          ].includes(job_view.job.status) &&
          !job_view.num_partners_offer_is_visible_to,
      );
      if (jobs.length < 1) {
        return;
      }
      const results = await Promise.all(
        jobs.map((job) =>
          axios
            .get(
              `/api/admin/jobs/${job.job.sfid}/num_partners_offer_is_visible_to`,
            )
            .then(({ data }) => ({ job, data }))
            .catch(() => null),
        ),
      );
      results.filter(Boolean).forEach(({ job, data }) => {
        this.$set(
          job,
          'num_partners_offer_is_visible_to',
          data.num_partners_offer_is_visible_to,
        );
        this.$set(job, 'num_partners_potential', data.num_partners_potential);
      });
    },
    async loadProductOrders(items) {
      const jobIds = items.map(({ sfid }) => sfid);
      if (jobIds.length == 0) {
        return;
      }
      const pendingQuery = RequestQueryBuilder.create({
        search: {
          'JobView.id': { [CondOperator.IN]: jobIds },
        },
      }).query();
      const orderedQuery = RequestQueryBuilder.create({
        search: {
          'job.id': { [CondOperator.IN]: jobIds },
        },
      }).query();
      let pending, ordered;
      try {
        pending = await axios
          .get(`/documents-api/product-orders-pending?${pendingQuery}`)
          .then(({ data }) => data);
        ordered = await axios
          .get(`/documents-api/product-orders?${orderedQuery}`)
          .then(({ data }) => data);
      } catch {
        return;
      }
      // Only the rows this call asked about — the responses say nothing about
      // pages loaded earlier, and re-scanning them just rewrites what they
      // already have.
      items.forEach((job) => {
        const pendingOrder = pending.filter(
          (order) => order.job.id == job.sfid,
        );
        const orderedOrders = ordered.filter(
          (order) => order.job.id == job.sfid,
        );
        if (pendingOrder.length || orderedOrders.length) {
          let info = {};
          if (orderedOrders.length == 0) {
            info = { status: 'none', variant: 'danger' };
          } else if (pendingOrder.length == 0) {
            info = { status: 'all', variant: 'success' };
          } else {
            info = { status: 'some', variant: 'warning' };
          }

          info.orderDetails = [
            ...new Set(
              orderedOrders
                ?.sort(
                  (firstEl, secondEl) =>
                    secondEl.created_at - firstEl.created_at,
                )
                .map((order) => ({
                  items: order.items.map(({ productName }) => productName),
                  url: order?.carrier?.urlPattern?.replace(
                    '{ID}',
                    order.trackingID,
                  ),
                  trackingID: order.trackingID,
                  shipTo: order.shipTo,
                })),
            ),
          ];
          info.trackingLink = [
            ...new Set(
              orderedOrders
                .sort(
                  (firstEl, secondEl) =>
                    secondEl.created_at - firstEl.created_at,
                )
                .map((order) =>
                  order?.carrier?.urlPattern?.replace('{ID}', order.trackingID),
                ),
            ),
          ];
          info.shipTo = [
            ...new Set(
              orderedOrders
                .filter(({ shipTo }) => shipTo !== null || shipTo !== undefined)
                .map(({ shipTo }) => shipTo),
            ),
          ];
          this.$set(job, 'productOrder', info);
        }
      });
    },
    compoundStateItems(job_view) {
      return mapDetails.call(
        this,
        job_view.job.states?.list?.[0]?.startsWith(job_view.job.status) &&
          job_view.job.states?.list?.[0].includes('.')
          ? job_view.job.states?.list
          : [],
        job_view.job.states?.details,
      );
    },
    opportunityHint(item) {
      return getOpportunityHint(item.opportunity, this.locale);
    },
    selectActiveStatuses() {
      this.filterStatus = this.statuses.filter(
        (status) =>
          ![
            JobStatusEnum.NEW,
            JobStatusEnum.CANCELLED,
            JobStatusEnum.CLOSED,
          ].includes(status.value),
      );
    },
    // --- Job presets --------------------------------------------------------
    fetchPresetCounts() {
      this.$store.dispatch('jobPresets/fetchCounts', {
        requests: presetCountRequests(),
      });
    },
    // Money totals sit next to a count in tight chrome (dropdown rows, the
    // results pill), so they render in whole kEUR. The exact amount always goes
    // in a tooltip so the rounding never hides the real figure.
    formatKEur(value) {
      if (value == null) return null;
      const thousands = new Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 0,
      }).format(value / 1000);
      return `${thousands} kEUR`;
    },
    formatEur(value) {
      if (value == null) return null;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(value);
    },
    presetRevenue(id) {
      return this.formatKEur(this.presetRevenues[id]);
    },
    presetRevenueTitle(id) {
      const exact = this.formatEur(this.presetRevenues[id]);
      if (exact == null) return null;
      return this.$t('job-presets.revenue-total', { amount: exact });
    },
    // Selecting a preset (or "All") only drives the URL; the $route watcher does
    // the actual filter application, so sidebar links and on-page pills share
    // one code path.
    selectPreset(id) {
      const query = id ? { view: id } : {};
      if ((this.$route.query.view || null) === (id || null)) {
        // Same preset re-clicked — re-apply directly (no route change to watch).
        this.applyView(id, this.$route.query.view || null);
        return;
      }
      this.$router.push({ path: '/jobs', query }).catch(() => {});
    },
    applyPreset(preset) {
      const statusText = (value) =>
        (this.statuses.find((s) => s.value === value) || {}).text || value;
      this.$store.commit(
        SET_JOB_FILTER,
        presetToStoreFilter(preset, statusText),
      );
    },
    clearPresetFilters() {
      this.$store.commit(SET_JOB_FILTER, {
        search: '',
        owner: null,
        status: [],
        useAdvancedSearch: false,
        advancedSearch: null,
        useQuickSearch: false,
        quickSearch: null,
      });
    },
    // Apply whatever the URL asks for. `prevView` distinguishes a passive mount
    // (no previous preset -> leave persisted filters untouched) from an explicit
    // switch to "All" (had a preset, now cleared -> reset filters).
    applyView(view, prevView) {
      if (view) {
        const preset = getPreset(view);
        if (preset) this.applyPreset(preset);
      } else if (prevView) {
        this.clearPresetFilters();
      }
    },
  },
  watch: {
    // Preset selection changes only the URL; react to it here (covers sidebar
    // links and on-page pills, and navigation while already on /jobs).
    '$route.query.view'(view, prevView) {
      this.applyView(view || null, prevView || null);
    },
    // Navigating to another /jobs/<job-name> while this view is already mounted
    // (the route param changes, the component doesn't remount) opens that job as
    // the active tab, next to the ones already open.
    job_id(name) {
      if (name) this.openJobByName(name);
    },
    filterUseAdvancedSearch(nextVal, prevVal) {
      const thisPrev = JSON.stringify(this.filterAdvancedSearch);
      if (prevVal && this.filterAdvancedSearch) {
        Object.keys(this.filterAdvancedSearch).forEach((key) => {
          this.filterAdvancedSearch[key] = [];
        });
      }
      const thisNext = JSON.stringify(this.filterAdvancedSearch);
      if (thisPrev !== thisNext) {
        this.$nextTick(() => this.loadData());
      }
    },
    filterUseQuickSearch(nextVal, prevVal) {
      const thisPrev = JSON.stringify(this.filterQuickSearch);
      if (prevVal && this.filterQuickSearch) {
        Object.keys(this.filterQuickSearch).forEach((key) => {
          this.filterQuickSearch[key] = [];
        });
      }
      const thisNext = JSON.stringify(this.filterQuickSearch);
      if (thisPrev !== thisNext) {
        this.$nextTick(() => this.loadData());
      }
    },
    sortBy() {
      this.loadData();
    },
    sortDir() {
      this.loadData();
    },
    filterSearch(nextVal, prevVal) {
      if (normalizeSearchInput(prevVal) !== normalizeSearchInput(nextVal)) {
        this.$nextTick(() => this.loadData());
      }
    },
    filterAdvancedSearch(nextVal, prevVal) {
      if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
        this.$nextTick(() => this.loadData());
      }
    },
    filterQuickSearch(nextVal, prevVal) {
      if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
        this.$nextTick(() => this.loadData());
      }
    },
    filterOwner(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.$nextTick(() => this.loadData());
      }
    },
    filterStatus(nextVal, prevVal) {
      if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
        this.$nextTick(() => this.loadData());
      }
    },
    filterTest(nextVal, prevVal) {
      if (prevVal !== nextVal) {
        this.$nextTick(() => this.loadData());
      }
    },
  },
  created() {
    // Apply a preset from ?view= before the first load so the initial request
    // already carries the preset's filters (no wasted round-trip).
    this.applyView(this.$route.query.view || null, null);
    this.fetchPresetCounts();
    this.loadData();
    // `/jobs/<job-name>`: the list above loads unfiltered as usual, and the
    // deep-linked job opens next to it as the active console tab.
    if (this.job_id) {
      this.openJobByName(this.job_id);
    }
    axios.get(`/api/admin/jobs/statuses`).then((response) => {
      this.statuses = response.data.map((status) => ({
        text:
          status.gid_name?.[this.locale] ||
          status.gid_name?.['en'] ||
          status.job_status,
        value: status.job_status,
      }));
    });

    const gidAccountId = 1;
    const queryString = RequestQueryBuilder.create({
      filter: [
        {
          field: 'accountId',
          operator: CondOperator.EQUALS,
          value: gidAccountId,
        },
      ],
      fields: ['id', 'name', 'isDeleted', 'profiles'],
      sort: [{ field: 'name', order: 'ASC' }],
    }).query();

    axios.get(`/data-api/contact?${queryString}`).then((response) => {
      if (response.data.length) {
        this.owners = response.data
          .filter((record) => !record.isDeleted)
          .filter((record) =>
            record.profiles.some(
              ({ profile }) =>
                profile === ContactProfileEnum.PROJECT_MANAGER_GENERIC,
            ),
          )
          .map(({ id, name }) => ({
            value: id,
            text: name,
          }));
      }
    });

    axios
      .get('/data-api/sf-picklist', {
        params: {
          api_names: 'job_media_file_types',
          values_only: true,
        },
      })
      .then((response) => {
        this.jmfPicklist = (
          response.data?.job_media_file_types?.Metadata?.customValue || []
        ).sort((a, b) => a.label.localeCompare(b.label));
      });
  },
};
</script>
<style lang="scss" scoped>
// ---- Job preset dropdown ---------------------------------------------------
.gid-job-presets {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.25rem 0;
  margin-top: 0.5rem;
}

.gid-job-presets__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #605e5c;
  white-space: nowrap;
}

// Small "?" affordance next to the preset picker: opens the status/substatus
// reference. Sized to sit on the dropdown's baseline without adding row height.
.gid-status-guide__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--gid-border, #e1dfdd);
  border-radius: 50%;
  background: transparent;
  color: #605e5c;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  .material-icons {
    font-size: 16px;
    line-height: 1;
  }

  &:hover,
  &:focus-visible {
    color: #9a6500;
    border-color: #c8820a;
    background: rgba(200, 130, 10, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #c8820a;
    outline-offset: 1px;
  }
}

.gid-job-presets__dd {
  min-width: 260px;

  // The trigger renders the active preset (+ its count) inline.
  ::v-deep > .btn {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    text-align: left;
  }
}

// Grouped menu: headers separate the lifecycle phases; each item shows its
// title with the revenue total + live count pushed to the right.
.gid-job-presets__menu {
  min-width: 350px;
  max-height: 70vh;
  overflow-y: auto;
}

.gid-preset-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.gid-preset-item__label {
  // Absorb the free space so the revenue + count stay together on the right,
  // and let a long title truncate instead of pushing them out of the row.
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gid-preset-item__revenue {
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  color: #605e5c;
  white-space: nowrap;
}

// Revenue / cost share the results pill with the row count (the pill itself is
// styled globally in vue-common). Muted and rule-separated so the three figures
// read as one group instead of competing.
.gid-results-count__money {
  margin-left: 0.6rem;
  padding-left: 0.6rem;
  border-left: 1px solid rgba(255, 255, 255, 0.35);
  font-size: 0.85em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.gid-preset-item__count {
  flex: 0 0 auto;
  background: #edebe9;
  color: #605e5c;
  font-size: 0.72rem;
}

// ---- Job console tab strip -------------------------------------------------
.gid-job-tabs {
  position: sticky;
  top: var(--header-height, 0px);
  z-index: 1020;
  display: flex;
  align-items: stretch;
  gap: 2px;
  margin: 0;
  padding: 0.5rem 0.25rem 0;
  background: #faf9f8;
  border-bottom: 1px solid #e1dfdd;
  box-shadow: 0 4px 6px -4px rgba(0, 0, 0, 0.12);
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}

.gid-job-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
  max-width: 240px;
  padding: 0.5rem 0.75rem;
  border: 1px solid transparent;
  border-bottom: none;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background: transparent;
  color: #605e5c;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: #f3f2f1;
    color: #201a12;
  }

  &--active {
    background: #ffffff;
    color: #1a1100;
    border-color: #e1dfdd;
    box-shadow: inset 0 2px 0 0 #c8820a; // amber top accent
    margin-bottom: -1px;
  }
}

.gid-job-tab__icon {
  font-size: 1.05rem;
  line-height: 1;
}

.gid-job-tab__no {
  overflow: hidden;
  text-overflow: ellipsis;
}

.gid-job-tab__cust {
  color: #8a8886;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gid-job-tab--active .gid-job-tab__cust {
  color: #605e5c;
}

.gid-job-tab__dirty {
  color: #c8820a;
  font-size: 0.7rem;
  line-height: 1;
}

.gid-job-tab__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  margin-left: 0.15rem;
  border-radius: 3px;
  font-size: 1.1rem;
  line-height: 1;
  color: #8a8886;

  &:hover {
    background: #e1dfdd;
    color: #a4262c;
  }
}

.gid-job-pane {
  padding-top: 0.5rem;
}

// Dark theme (cool slate, aligned with the site's "Nah am Feld" section)
.dark-mode .gid-job-tabs {
  background: #141820;
  border-bottom-color: #333c4a;
  box-shadow: 0 4px 6px -4px rgba(0, 0, 0, 0.5);
}
.dark-mode .gid-job-tab {
  color: #9aa5b4;

  &:hover {
    background: #202634;
    color: #eef1f6;
  }

  &--active {
    background: #202634;
    color: #eef1f6;
    border-color: #333c4a;
    box-shadow: inset 0 2px 0 0 #e6a52e;
  }
}
.dark-mode .gid-job-tab__cust {
  color: #7a8494;
}
.dark-mode .gid-job-tab--active .gid-job-tab__cust {
  color: #9aa5b4;
}
.dark-mode .gid-job-tab__dirty {
  color: #e6a52e;
}
.dark-mode .gid-status-guide__btn {
  color: #9aa5b4;
  border-color: #333c4a;

  &:hover,
  &:focus-visible {
    color: #f0bc5c;
    border-color: #e6a52e;
    background: rgba(230, 165, 46, 0.12);
  }

  &:focus-visible {
    outline-color: #e6a52e;
  }
}
.dark-mode .gid-job-tab__close:hover {
  background: #333c4a;
  color: #f2776b;
}

.icon-grad {
  background: #04a789;
  background: linear-gradient(
    49deg,
    rgba(2, 51, 36, 0.78) 0%,
    rgba(4, 167, 137, 1) 100%
  );
  border: 0px;
  color: white;
  transition: 0.9s;
  opacity: 0.4;
}
.icon-grad:hover {
  background: linear-gradient(
    49deg,
    rgba(2, 0, 36, 0.8) 0%,
    rgba(4, 167, 137, 1) 100%
  );
  opacity: 1;
}
.clickable-text {
  color: #212529;
  cursor: pointer;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: #000;
    text-decoration: underline;
  }
}
</style>
<style lang="scss">
// b-modal renders into a portal at the end of <body>, so these cannot be
// scoped. The guide is a full document — give it the whole modal body and let
// it scroll itself rather than the modal.
.gid-status-guide__body {
  padding: 0;
  height: 78vh;
  overflow: hidden;
}

.gid-status-guide__frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.dark-mode .clickable-text {
  color: #e2e8f0 !important;

  &:hover {
    color: #5ba8d4 !important;
  }
}
</style>
